import { useCallback, useMemo, useState } from "react";

import { IAccordionSelection } from "./types";

type Count = {
	[id: string]: number;
};

const initData = (options: IAccordionSelection[]) =>
	options.reduce<Count>((count, { id }) => {
		count[id] = 0;

		return count;
	}, {});

export const useAccordionQtyCount = (
	add: (id: string) => void,
	remove: (id: string) => void,
	selected: string[],
	max: number,
	options: IAccordionSelection[]
) => {
	const [counter, setCounter] = useState<Count>(() => initData(options));

	const currentMax = useMemo(() => {
		return options.reduce((total, { id }) => {
			return total + (counter[id] ?? 0);
		}, 0);
	}, [counter, options]);

	const increment = useCallback(
		(selection_id: string) => {
			setCounter((prev) => {
				const newprev = { ...prev };

				newprev[selection_id]++;

				return newprev;
			});
		},
		[options, currentMax, max]
	);

	const decrement = useCallback(
		(selection_id: string) => {
			setCounter((prev) => {
				const newprev = { ...prev };

				newprev[selection_id]--;
				if (newprev[selection_id] < 0) newprev[selection_id] = 0;

				return newprev;
			});
		},
		[options]
	);

	const changeQtyForSelection = useCallback(
		(selection_id: string) => {
			const fn = (value: number) => {
				if (value > counter[selection_id]) {
					add(selection_id);
					increment(selection_id);
				} else {
					remove(selection_id);
					decrement(selection_id);
				}
			};

			return fn;
		},
		[add, counter, increment, remove, decrement]
	);

	const maxvalues = useMemo(() => {
		return options.reduce<Count>((m, { id }) => {
			m[id] = max - selected.filter((sid) => sid !== id).length;

			return m;
		}, {});
	}, [selected, options, max]);

	return {
		changeQtyForSelection,
		counter,
		maxvalues,
	};
};
